import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Separator } from '../../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Switch } from '../../ui/switch';
import { 
  Settings,
  Plus,
  Edit,
  Trash2,
  Save,
  ArrowLeft,
  Eye,
  Clock,
  Users,
  Target,
  FileText,
  AlertCircle,
  CheckCircle,
  Lightbulb,
  Play,
  BarChart3,
  Shuffle,
  Timer,
  Award,
  MessageCircle,
  Building,
  Globe
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../ui/use-toast';
import axios from 'axios';

const ConfiguracaoAvaliacao = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [valores, setValores] = useState([]);
  const [valoresIdentidade, setValoresIdentidade] = useState([]);
  const [stats, setStats] = useState({});
  
  // Estados do modal de criação/edição
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingAvaliacao, setEditingAvaliacao] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo_avaliacao: 'candidato',
    escala_resposta: 'likert_5',
    peso_cultura_vs_tecnica: 0.6,
    perguntas: [],
    tempo_limite_minutos: null,
    randomizar_perguntas: false,
    mostrar_resultado_imediato: true,
    criterio_aprovacao: 0.7
  });

  // Estados do editor de perguntas
  const [isAddPerguntaOpen, setIsAddPerguntaOpen] = useState(false);
  const [novaPergunta, setNovaPergunta] = useState({
    texto: '',
    tipo: 'likert',
    opcoes: [],
    valor_organizacional_id: 'none',
    peso: 3,
    obrigatoria: true
  });

  // Estados para geração com IA
  const [isChoiceModalOpen, setIsChoiceModalOpen] = useState(false);
  const [isIAModalOpen, setIsIAModalOpen] = useState(false);
  const [isGeneratingIA, setIsGeneratingIA] = useState(false);
  const [iaFormData, setIaFormData] = useState({
    nome: '',
    quantidade_perguntas: 30,
    tipo: 'candidato'
  });
  const [avaliacaoGeradaIA, setAvaliacaoGeradaIA] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Configurações das escalas de resposta
  const escalasResposta = {
    'likert_5': { nome: 'Likert 5 pontos', descricao: '1-Discordo Totalmente a 5-Concordo Totalmente' },
    'likert_7': { nome: 'Likert 7 pontos', descricao: '1-Discordo Totalmente a 7-Concordo Totalmente' },
    'sim_nao': { nome: 'Sim/Não', descricao: 'Resposta binária simples' },
    'cenario': { nome: 'Cenários', descricao: 'Múltipla escolha baseada em situações' }
  };

  const tiposAvaliacao = {
    'candidato': { nome: 'Candidatos', descricao: 'Para processos seletivos', icon: Users, color: 'blue' },
    'colaborador': { nome: 'Colaboradores', descricao: 'Para avaliação interna', icon: Target, color: 'green' },
    'promocao': { nome: 'Promoção', descricao: 'Para mudanças de cargo', icon: Award, color: 'purple' }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [avaliacoesResponse, valoresResponse, statsResponse, identidadeResponse] = await Promise.all([
        axios.get(`${backendUrl}/api/fit-cultural/avaliacoes`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${backendUrl}/api/fit-cultural/valores`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${backendUrl}/api/fit-cultural/avaliacoes/stats`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${backendUrl}/api/profile/identidade-organizacional`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => ({ data: null })) // Não falhar se não existir
      ]);

      setAvaliacoes(avaliacoesResponse.data);
      setValores(valoresResponse.data);
      setStats(statsResponse.data);
      
      // Extrair valores da Identidade Organizacional
      if (identidadeResponse.data && identidadeResponse.data.valores) {
        setValoresIdentidade(identidadeResponse.data.valores);
      } else {
        setValoresIdentidade([]);
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados das avaliações",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validações
      if (!formData.nome.trim() || !formData.descricao.trim()) {
        toast({
          title: "Campos Obrigatórios",
          description: "Nome e Descrição são obrigatórios",
          variant: "destructive"
        });
        return;
      }

      if (formData.perguntas.length === 0) {
        toast({
          title: "Perguntas Obrigatórias",
          description: "Adicione pelo menos uma pergunta à avaliação",
          variant: "destructive"
        });
        return;
      }

      if (editingAvaliacao) {
        // Atualizar
        await axios.put(
          `${backendUrl}/api/fit-cultural/avaliacoes/${editingAvaliacao.id}`,
          formData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        
        toast({
          title: "Sucesso!",
          description: "Avaliação atualizada com sucesso",
        });
      } else {
        // Criar
        await axios.post(
          `${backendUrl}/api/fit-cultural/avaliacoes`,
          formData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        
        toast({
          title: "Sucesso!",
          description: "Avaliação criada com sucesso",
        });
      }

      await loadData();
      handleCloseModal();

    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar avaliação",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (avaliacao) => {
    if (!window.confirm(`Tem certeza que deseja excluir a avaliação "${avaliacao.nome}"?`)) {
      return;
    }

    try {
      await axios.delete(
        `${backendUrl}/api/fit-cultural/avaliacoes/${avaliacao.id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast({
        title: "Sucesso!",
        description: "Avaliação excluída com sucesso",
      });
      
      await loadData();
      
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir avaliação",
        variant: "destructive"
      });
    }
  };

  // Função para gerar avaliação com IA
  const handleGerarComIA = async () => {
    try {
      setIsGeneratingIA(true);
      
      // Validações
      if (!iaFormData.nome.trim()) {
        toast({
          title: "Campo Obrigatório",
          description: "Nome da avaliação é obrigatório",
          variant: "destructive"
        });
        return;
      }

      if (iaFormData.quantidade_perguntas < 10 || iaFormData.quantidade_perguntas > 50) {
        toast({
          title: "Quantidade Inválida",
          description: "A quantidade de perguntas deve estar entre 10 e 50",
          variant: "destructive"
        });
        return;
      }

      const response = await axios.post(
        `${backendUrl}/api/fit-cultural/gerar-avaliacao-ia`,
        iaFormData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      // Armazenar a avaliação gerada
      setAvaliacaoGeradaIA(response.data);
      
      // Fechar modal de IA e abrir preview
      setIsIAModalOpen(false);
      setIsPreviewOpen(true);
      
      toast({
        title: "✅ Avaliação Gerada!",
        description: `${response.data.total_perguntas} perguntas criadas (${response.data.perguntas_invertidas} invertidas)`,
      });

    } catch (error) {
      console.error('Erro ao gerar com IA:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Erro ao gerar avaliação com IA. Verifique se a Identidade Organizacional está configurada.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingIA(false);
    }
  };

  // Função para abrir o modal de criação manual
  const handleCriarManualmente = () => {
    setIsChoiceModalOpen(false);
    setEditingAvaliacao(null);
    setFormData({
      nome: '',
      descricao: '',
      tipo_avaliacao: 'candidato',
      escala_resposta: 'likert_5',
      peso_cultura_vs_tecnica: 0.6,
      perguntas: [],
      tempo_limite_minutos: null,
      randomizar_perguntas: false,
      mostrar_resultado_imediato: true,
      criterio_aprovacao: 0.7
    });
    setIsModalOpen(true);
  };

  // Função para abrir o modal de IA
  const handleCriarComIA = () => {
    // Verificar se há valores na Identidade Organizacional
    if (!valoresIdentidade || valoresIdentidade.length < 2) {
      toast({
        title: "Configuração Necessária",
        description: "É necessário ter pelo menos 2 valores cadastrados na Identidade Organizacional para gerar uma avaliação com IA. Configure em Profiler → Identidade Organizacional.",
        variant: "destructive"
      });
      return;
    }
    
    setIsChoiceModalOpen(false);
    setIaFormData({
      nome: '',
      quantidade_perguntas: 30,
      tipo: 'candidato'
    });
    setIsIAModalOpen(true);
  };

  // Função para salvar avaliação gerada pela IA
  const handleSalvarAvaliacaoIA = async () => {
    try {
      setSaving(true);
      
      // Função auxiliar para limpar valores null/undefined
      const cleanObject = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(item => cleanObject(item));
        }
        
        if (obj && typeof obj === 'object') {
          const cleaned = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== null && value !== undefined) {
              cleaned[key] = cleanObject(value);
            }
          }
          return cleaned;
        }
        
        return obj;
      };
      
      // Extrair apenas os campos que o modelo espera
      const avaliacaoParaSalvar = {
        nome: avaliacaoGeradaIA.nome,
        descricao: avaliacaoGeradaIA.descricao,
        tipo_avaliacao: avaliacaoGeradaIA.tipo_avaliacao || 'candidato',
        escala_resposta: avaliacaoGeradaIA.escala_resposta || 'likert_5',
        peso_cultura_vs_tecnica: avaliacaoGeradaIA.peso_cultura_vs_tecnica || 0.6,
        perguntas: avaliacaoGeradaIA.perguntas.map(p => {
          const pergunta = {
            texto: p.texto,
            tipo: p.tipo || 'likert'
          };
          
          // Só incluir valor_organizacional_id se for uma string válida (não vazia)
          if (p.valor_organizacional_id && typeof p.valor_organizacional_id === 'string' && p.valor_organizacional_id.trim() !== '') {
            pergunta.valor_organizacional_id = p.valor_organizacional_id;
          }
          
          // Incluir outros campos opcionais apenas se definidos
          if (p.peso !== undefined && p.peso !== null) {
            pergunta.peso = p.peso;
          }
          
          if (p.obrigatoria !== undefined && p.obrigatoria !== null) {
            pergunta.obrigatoria = p.obrigatoria;
          }
          
          if (p.invertida !== undefined && p.invertida !== null) {
            pergunta.invertida = p.invertida;
          }
          
          if (p.opcoes && Array.isArray(p.opcoes) && p.opcoes.length > 0) {
            pergunta.opcoes = p.opcoes;
          }
          
          return pergunta;
        }),
        randomizar_perguntas: avaliacaoGeradaIA.randomizar_perguntas || false,
        mostrar_resultado_imediato: avaliacaoGeradaIA.mostrar_resultado_imediato !== undefined 
          ? avaliacaoGeradaIA.mostrar_resultado_imediato 
          : true,
        criterio_aprovacao: avaliacaoGeradaIA.criterio_aprovacao || 0.7
      };
      
      // Incluir tempo_limite_minutos apenas se tiver valor
      if (avaliacaoGeradaIA.tempo_limite_minutos && avaliacaoGeradaIA.tempo_limite_minutos > 0) {
        avaliacaoParaSalvar.tempo_limite_minutos = avaliacaoGeradaIA.tempo_limite_minutos;
      }
      
      console.log('📤 Enviando para backend:', JSON.stringify(avaliacaoParaSalvar, null, 2));
      
      await axios.post(
        `${backendUrl}/api/fit-cultural/avaliacoes`,
        avaliacaoParaSalvar,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast({
        title: "Sucesso!",
        description: "Avaliação criada com sucesso",
      });

      await loadData();
      setIsPreviewOpen(false);
      setAvaliacaoGeradaIA(null);

    } catch (error) {
      console.error('❌ Erro ao salvar:', error);
      console.error('📋 Resposta do servidor:', error.response?.data);
      
      // Extrair mensagem de erro apropriada
      let errorMessage = "Erro ao salvar avaliação";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Se detail for um array de erros de validação
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return `${err.loc ? err.loc.join('.') + ': ' : ''}${err.msg}`;
            return JSON.stringify(err);
          }).join('; ');
        } 
        // Se detail for uma string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Se detail for um objeto
        else {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast({
        title: "Erro ao Salvar",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Função para editar avaliação gerada pela IA
  const handleEditarAvaliacaoIA = () => {
    // Converter para formato de edição manual
    setFormData({
      nome: avaliacaoGeradaIA.nome,
      descricao: avaliacaoGeradaIA.descricao,
      tipo_avaliacao: avaliacaoGeradaIA.tipo_avaliacao,
      escala_resposta: avaliacaoGeradaIA.escala_resposta,
      peso_cultura_vs_tecnica: avaliacaoGeradaIA.peso_cultura_vs_tecnica,
      perguntas: avaliacaoGeradaIA.perguntas,
      tempo_limite_minutos: avaliacaoGeradaIA.tempo_limite_minutos,
      randomizar_perguntas: avaliacaoGeradaIA.randomizar_perguntas,
      mostrar_resultado_imediato: avaliacaoGeradaIA.mostrar_resultado_imediato,
      criterio_aprovacao: avaliacaoGeradaIA.criterio_aprovacao
    });
    setIsPreviewOpen(false);
    setIsModalOpen(true);
  };

  const handleEdit = (avaliacao) => {
    setEditingAvaliacao(avaliacao);
    setFormData({
      nome: avaliacao.nome,
      descricao: avaliacao.descricao,
      tipo_avaliacao: avaliacao.tipo_avaliacao,
      escala_resposta: avaliacao.escala_resposta,
      peso_cultura_vs_tecnica: avaliacao.peso_cultura_vs_tecnica,
      perguntas: avaliacao.perguntas || [],
      tempo_limite_minutos: avaliacao.tempo_limite_minutos,
      randomizar_perguntas: avaliacao.randomizar_perguntas,
      mostrar_resultado_imediato: avaliacao.mostrar_resultado_imediato,
      criterio_aprovacao: avaliacao.criterio_aprovacao
    });
    setIsModalOpen(true);
  };

  const handlePreview = async (avaliacao) => {
    try {
      const response = await axios.post(
        `${backendUrl}/api/fit-cultural/avaliacoes/${avaliacao.id}/preview`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setPreviewData(response.data);
      setIsPreviewOpen(true);
      
    } catch (error) {
      console.error('Erro ao gerar preview:', error);
      toast({
        title: "Erro",
        description: "Erro ao gerar preview da avaliação",
        variant: "destructive"
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAvaliacao(null);
    setFormData({
      nome: '',
      descricao: '',
      tipo_avaliacao: 'candidato',
      escala_resposta: 'likert_5',
      peso_cultura_vs_tecnica: 0.6,
      perguntas: [],
      tempo_limite_minutos: null,
      randomizar_perguntas: false,
      mostrar_resultado_imediato: true,
      criterio_aprovacao: 0.7
    });
  };

  const adicionarPergunta = () => {
    if (!novaPergunta.texto.trim()) {
      toast({
        title: "Erro",
        description: "O texto da pergunta é obrigatório",
        variant: "destructive"
      });
      return;
    }

    const pergunta = {
      ...novaPergunta,
      id: Date.now().toString(),
      valor_organizacional_id: novaPergunta.valor_organizacional_id === 'none' ? null : novaPergunta.valor_organizacional_id
    };

    setFormData(prev => ({
      ...prev,
      perguntas: [...prev.perguntas, pergunta]
    }));

    setNovaPergunta({
      texto: '',
      tipo: 'likert',
      opcoes: [],
      valor_organizacional_id: 'none',
      peso: 3,
      obrigatoria: true
    });

    setIsAddPerguntaOpen(false);
    
    toast({
      title: "Sucesso!",
      description: "Pergunta adicionada à avaliação",
    });
  };

  const removerPergunta = (index) => {
    setFormData(prev => ({
      ...prev,
      perguntas: prev.perguntas.filter((_, i) => i !== index)
    }));
  };

  const gerarPerguntasAutomaticas = async () => {
    try {
      const response = await axios.post(
        `${backendUrl}/api/fit-cultural/avaliacoes/gerar-perguntas-automaticas`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      const perguntasSugeridas = response.data.perguntas_sugeridas.map((p, index) => ({
        ...p,
        id: (Date.now() + index).toString()
      }));

      setFormData(prev => ({
        ...prev,
        perguntas: [...prev.perguntas, ...perguntasSugeridas]
      }));

      toast({
        title: "Sucesso!",
        description: `${perguntasSugeridas.length} perguntas geradas automaticamente`,
      });
      
    } catch (error) {
      console.error('Erro ao gerar perguntas:', error);
      toast({
        title: "Erro",
        description: "Erro ao gerar perguntas automáticas",
        variant: "destructive"
      });
    }
  };

  const getValorById = (valorId) => {
    return valores.find(v => v.id === valorId);
  };

  const criarTemplateCientifico = async (tipoTemplate) => {
    try {
      setSaving(true);
      
      const response = await axios.post(
        `${backendUrl}/api/fit-cultural/avaliacoes/criar-template-cientifico?template_id=${tipoTemplate}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast({
        title: "Sucesso!",
        description: `Template ${tipoTemplate} criado com sucesso`,
      });
      
      await loadData();
      
    } catch (error) {
      console.error('Erro ao criar template científico:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar template científico",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="p-2"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-8 h-8 text-blue-600" />
              Configuração de Avaliações
            </h1>
            <p className="text-gray-600 mt-1">
              Configure questionários personalizados para avaliação de fit cultural
            </p>
          </div>
          <Button onClick={() => setIsChoiceModalOpen(true)} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Nova Avaliação
          </Button>
        </div>

        {/* Cards de Estatísticas Modernos com Gradientes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-blue-800">Total de Avaliações</CardTitle>
              <FileText className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-900">{stats.total_avaliacoes || 0}</div>
              <p className="text-xs text-blue-700 mt-1">Avaliações configuradas</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-green-800">Avaliações Públicas</CardTitle>
              <Eye className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-900">{stats.avaliacoes_publicas || 0}</div>
              <p className="text-xs text-green-700 mt-1">Disponíveis publicamente</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-purple-800">Valores Cadastrados</CardTitle>
              <Target className="h-5 w-5 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-900">{valores.length || 0}</div>
              <p className="text-xs text-purple-700 mt-1">Para vincular às perguntas</p>
            </CardContent>
          </Card>
        </div>

        {/* Avaliações Criadas - Cards Coloridos */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Avaliações Criadas ({avaliacoes.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {avaliacoes.map((avaliacao, index) => {
              // Paleta de cores gradientes
              const cores = [
                'from-blue-400 to-blue-600',      // Azul
                'from-green-400 to-green-600',    // Verde
                'from-purple-400 to-purple-600',  // Roxo
                'from-pink-400 to-pink-600',      // Rosa
                'from-orange-400 to-orange-600',  // Laranja
                'from-teal-400 to-teal-600',      // Teal
                'from-indigo-400 to-indigo-600',  // Indigo
                'from-red-400 to-red-600'         // Vermelho
              ];
              const corGradiente = cores[index % cores.length];
              
              return (
                <Card key={avaliacao.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  {/* Header colorido */}
                  <div className={`bg-gradient-to-r ${corGradiente} p-6 text-white`}>
                    <h3 className="text-xl font-bold mb-2">{avaliacao.nome}</h3>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-white/20 text-white border-white/30">
                        {avaliacao.perguntas?.length || 0} perguntas
                      </Badge>
                      <Badge className="bg-white/20 text-white border-white/30 capitalize">
                        {avaliacao.tipo_avaliacao}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Conteúdo */}
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {avaliacao.descricao || 'Sem descrição disponível'}
                    </p>
                    
                    {/* Informações adicionais */}
                    <div className="space-y-2 text-xs text-gray-500 mb-4">
                      <div className="flex items-center justify-between">
                        <span>Escala:</span>
                        <span className="font-medium text-gray-700">
                          {avaliacao.escala_resposta === 'likert_5' ? 'Likert 5 pontos' : avaliacao.escala_resposta}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Critério:</span>
                        <span className="font-medium text-gray-700">
                          {Math.round((avaliacao.criterio_aprovacao || 0.7) * 100)}% mínimo
                        </span>
                      </div>
                      {avaliacao.tempo_limite_minutos && (
                        <div className="flex items-center justify-between">
                          <span>Tempo:</span>
                          <span className="font-medium text-gray-700">
                            {avaliacao.tempo_limite_minutos} minutos
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Ações */}
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleEdit(avaliacao)}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1"
                        onClick={() => handlePreview(avaliacao)}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Preview
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {/* Card para adicionar nova avaliação */}
            <Card className="border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50/50 transition-all duration-300 cursor-pointer flex items-center justify-center min-h-[300px]"
              onClick={() => setIsChoiceModalOpen(true)}
            >
              <div className="text-center p-6">
                <Plus className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Nova Avaliação</h3>
                <p className="text-sm text-gray-500">
                  Crie manualmente ou use IA
                </p>
              </div>
            </Card>
          </div>
        </div>

        {/* Lista de Avaliações */}
        <Card>
          <CardHeader>
            <CardTitle>Avaliações Configuradas ({avaliacoes.length})</CardTitle>
            <CardDescription>
              Gerencie seus questionários de fit cultural por tipo de aplicação
            </CardDescription>
          </CardHeader>
          <CardContent>
            {avaliacoes.length === 0 ? (
              <div className="text-center py-8">
                <Settings className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma avaliação configurada</h3>
                <p className="text-gray-600 mb-4">
                  Crie sua primeira avaliação de fit cultural
                </p>
                <Button onClick={() => setIsModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeira Avaliação
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Perguntas</TableHead>
                      <TableHead>Critério</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {avaliacoes.map((avaliacao) => {
                      const tipo = tiposAvaliacao[avaliacao.tipo_avaliacao];
                      const IconComponent = tipo?.icon || FileText;
                      
                      return (
                        <TableRow key={avaliacao.id}>
                          <TableCell className="font-medium">{avaliacao.nome}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="flex items-center gap-1 w-fit">
                              <IconComponent className="w-3 h-3" />
                              {tipo?.nome || avaliacao.tipo_avaliacao}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div className="font-medium">{avaliacao.perguntas?.length || 0} perguntas</div>
                              <div className="text-gray-500">{escalasResposta[avaliacao.escala_resposta]?.nome}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div className="font-medium">{Math.round(avaliacao.criterio_aprovacao * 100)}% mín.</div>
                              <div className="text-gray-500">
                                {Math.round(avaliacao.peso_cultura_vs_tecnica * 100)}% cultura
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={avaliacao.ativa ? "default" : "secondary"}>
                              {avaliacao.ativa ? "Ativa" : "Inativa"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePreview(avaliacao)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(avaliacao)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(avaliacao)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal de Criação/Edição */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingAvaliacao ? 'Editar Avaliação' : 'Nova Avaliação de Fit Cultural'}
              </DialogTitle>
              <DialogDescription>
                Configure um questionário personalizado para avaliação de fit cultural
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="basico" className="space-y-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basico">Configurações Básicas</TabsTrigger>
                <TabsTrigger value="perguntas">Perguntas ({formData.perguntas.length})</TabsTrigger>
                <TabsTrigger value="avancado">Configurações Avançadas</TabsTrigger>
              </TabsList>

              {/* Aba Básico */}
              <TabsContent value="basico" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome da Avaliação *</Label>
                    <Input
                      id="nome"
                      placeholder="Ex: Fit Cultural - Candidatos Tech"
                      value={formData.nome}
                      onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="tipo">Tipo de Avaliação *</Label>
                    <Select 
                      value={formData.tipo_avaliacao} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, tipo_avaliacao: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(tiposAvaliacao).map(([key, tipo]) => {
                          const IconComponent = tipo.icon;
                          return (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center gap-2">
                                <IconComponent className="w-4 h-4" />
                                <div>
                                  <div>{tipo.nome}</div>
                                  <div className="text-xs text-gray-500">{tipo.descricao}</div>
                                </div>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="descricao">Descrição *</Label>
                  <Textarea
                    id="descricao"
                    placeholder="Descreva o objetivo e contexto desta avaliação..."
                    value={formData.descricao}
                    onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                    className="min-h-[80px]"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="escala">Escala de Resposta</Label>
                    <Select 
                      value={formData.escala_resposta} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, escala_resposta: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(escalasResposta).map(([key, escala]) => (
                          <SelectItem key={key} value={key}>
                            <div>
                              <div>{escala.nome}</div>
                              <div className="text-xs text-gray-500">{escala.descricao}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="criterio">Critério de Aprovação (%)</Label>
                    <Input
                      id="criterio"
                      type="number"
                      min="0"
                      max="100"
                      value={Math.round(formData.criterio_aprovacao * 100)}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        criterio_aprovacao: parseInt(e.target.value) / 100 
                      }))}
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Aba Perguntas */}
              <TabsContent value="perguntas" className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">Perguntas da Avaliação</h3>
                    <p className="text-sm text-gray-600">
                      Configure as perguntas que serão apresentadas aos avaliados
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={gerarPerguntasAutomaticas}
                      className="flex items-center gap-2"
                    >
                      <Lightbulb className="w-4 h-4" />
                      Gerar Automático
                    </Button>
                    <Button onClick={() => setIsAddPerguntaOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Nova Pergunta
                    </Button>
                  </div>
                </div>

                {formData.perguntas.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                    <MessageCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma pergunta adicionada</h3>
                    <p className="text-gray-600 mb-4">
                      Adicione perguntas manualmente ou gere automaticamente baseado nos valores organizacionais
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.perguntas.map((pergunta, index) => {
                      const valor = getValorById(pergunta.valor_organizacional_id);
                      
                      return (
                        <div key={index} className="p-4 border rounded-lg bg-white">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs">
                                  {pergunta.tipo}
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  Peso: {pergunta.peso}
                                </Badge>
                                {pergunta.obrigatoria && (
                                  <Badge variant="destructive" className="text-xs">
                                    Obrigatória
                                  </Badge>
                                )}
                                {valor && (
                                  <Badge className="text-xs">
                                    {valor.nome}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm font-medium mb-2">{pergunta.texto}</p>
                              {pergunta.opcoes && pergunta.opcoes.length > 0 && (
                                <div className="text-xs text-gray-600">
                                  Opções: {pergunta.opcoes.join(', ')}
                                </div>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removerPergunta(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Modal para Nova Pergunta */}
                <Dialog open={isAddPerguntaOpen} onOpenChange={setIsAddPerguntaOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Nova Pergunta</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="texto-pergunta">Texto da Pergunta *</Label>
                        <Textarea
                          id="texto-pergunta"
                          placeholder="Digite a pergunta que será apresentada..."
                          value={novaPergunta.texto}
                          onChange={(e) => setNovaPergunta(prev => ({ ...prev, texto: e.target.value }))}
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="tipo-pergunta">Tipo</Label>
                          <Select 
                            value={novaPergunta.tipo} 
                            onValueChange={(value) => setNovaPergunta(prev => ({ ...prev, tipo: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="likert">Escala Likert</SelectItem>
                              <SelectItem value="multiple_choice">Múltipla Escolha</SelectItem>
                              <SelectItem value="text">Texto Livre</SelectItem>
                              <SelectItem value="scenario">Cenário</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label htmlFor="peso-pergunta">Peso (1-5)</Label>
                          <Select 
                            value={novaPergunta.peso.toString()} 
                            onValueChange={(value) => setNovaPergunta(prev => ({ ...prev, peso: parseInt(value) }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5].map(peso => (
                                <SelectItem key={peso} value={peso.toString()}>
                                  Peso {peso}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="valor-vinculado">Valor Organizacional Vinculado</Label>
                        <Select 
                          value={novaPergunta.valor_organizacional_id} 
                          onValueChange={(value) => setNovaPergunta(prev => ({ ...prev, valor_organizacional_id: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um valor..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nenhum valor específico</SelectItem>
                            {valores.map(valor => (
                              <SelectItem key={valor.id} value={valor.id}>
                                {valor.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAddPerguntaOpen(false)}>
                        Cancelar
                      </Button>
                      <Button onClick={adicionarPergunta}>
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </TabsContent>

              {/* Aba Avançado */}
              <TabsContent value="avancado" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Configurações de Tempo</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="tempo-limite">Tempo Limite (minutos)</Label>
                        <Input
                          id="tempo-limite"
                          type="number"
                          min="5"
                          placeholder="Sem limite"
                          value={formData.tempo_limite_minutos || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            tempo_limite_minutos: e.target.value ? parseInt(e.target.value) : null 
                          }))}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Comportamento da Avaliação</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="randomizar">Randomizar Perguntas</Label>
                          <p className="text-xs text-gray-600">Embaralhar ordem das perguntas</p>
                        </div>
                        <Switch
                          id="randomizar"
                          checked={formData.randomizar_perguntas}
                          onCheckedChange={(checked) => setFormData(prev => ({ 
                            ...prev, 
                            randomizar_perguntas: checked 
                          }))}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="resultado">Mostrar Resultado</Label>
                          <p className="text-xs text-gray-600">Exibir resultado imediatamente</p>
                        </div>
                        <Switch
                          id="resultado"
                          checked={formData.mostrar_resultado_imediato}
                          onCheckedChange={(checked) => setFormData(prev => ({ 
                            ...prev, 
                            mostrar_resultado_imediato: checked 
                          }))}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Peso Cultura vs. Técnica</CardTitle>
                    <CardDescription>
                      Defina a importância relativa entre fit cultural e competência técnica
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Cultura: {Math.round(formData.peso_cultura_vs_tecnica * 100)}%</span>
                        <span>Técnica: {Math.round((1 - formData.peso_cultura_vs_tecnica) * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={formData.peso_cultura_vs_tecnica}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          peso_cultura_vs_tecnica: parseFloat(e.target.value) 
                        }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingAvaliacao ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Preview */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Preview da Avaliação</DialogTitle>
              <DialogDescription>
                Visualização prévia de como a avaliação será apresentada
              </DialogDescription>
            </DialogHeader>
            
            {previewData && (
              <div className="space-y-4">
                {/* Estatísticas */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{previewData.estatisticas.total_perguntas}</div>
                    <div className="text-xs text-gray-600">Perguntas</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{previewData.estatisticas.tempo_estimado_minutos}</div>
                    <div className="text-xs text-gray-600">Minutos</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{previewData.estatisticas.valores_vinculados}</div>
                    <div className="text-xs text-gray-600">Valores</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{previewData.estatisticas.peso_total}</div>
                    <div className="text-xs text-gray-600">Peso Total</div>
                  </div>
                </div>

                {/* Validação */}
                <Alert className={previewData.validacao.valida ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
                  <CheckCircle className={`h-4 w-4 ${previewData.validacao.valida ? "text-green-600" : "text-red-600"}`} />
                  <AlertDescription className={previewData.validacao.valida ? "text-green-800" : "text-red-800"}>
                    <strong>Validação:</strong> {previewData.validacao.valida ? "Avaliação válida e pronta para uso" : "Avaliação precisa de ajustes"}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Escolha: Manual vs IA */}
        <Dialog open={isChoiceModalOpen} onOpenChange={setIsChoiceModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Criar Nova Avaliação
              </DialogTitle>
              <DialogDescription>
                Escolha como deseja criar sua avaliação de Fit Cultural
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              {/* Card: Criar Manualmente */}
              <Card 
                className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-blue-500"
                onClick={handleCriarManualmente}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-600">
                    <Edit className="w-5 h-5" />
                    Criar Manualmente
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Crie sua avaliação do zero com total controle sobre cada pergunta
                  </p>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>✓ Controle total das perguntas</li>
                    <li>✓ Personalização completa</li>
                    <li>✓ Configurações avançadas</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Card: Criar com IA */}
              <Card 
                className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-purple-500"
                onClick={handleCriarComIA}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-600">
                    <Lightbulb className="w-5 h-5" />
                    Criar com IA
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Gere automaticamente perguntas baseadas nos valores da sua empresa
                  </p>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>✓ Geração automática em segundos</li>
                    <li>✓ Baseado nos valores da empresa</li>
                    <li>✓ Perguntas inéditas e variadas</li>
                    <li>✓ Anti-trapaça incluído</li>
                  </ul>
                  <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                    Recomendado
                  </Badge>
                </CardContent>
              </Card>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Configuração IA */}
        <Dialog open={isIAModalOpen} onOpenChange={setIsIAModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-purple-600" />
                Criar Avaliação com IA
              </DialogTitle>
              <DialogDescription>
                Configure os parâmetros e deixe a IA gerar perguntas inéditas baseadas nos valores da empresa
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="ia-nome">Nome da Avaliação *</Label>
                <Input
                  id="ia-nome"
                  placeholder="Ex: Fit Cultural Tech 2025"
                  value={iaFormData.nome}
                  onChange={(e) => setIaFormData(prev => ({ ...prev, nome: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="ia-quantidade">Quantidade de Perguntas *</Label>
                <Input
                  id="ia-quantidade"
                  type="number"
                  min="10"
                  max="50"
                  placeholder="30"
                  value={iaFormData.quantidade_perguntas}
                  onChange={(e) => setIaFormData(prev => ({ ...prev, quantidade_perguntas: parseInt(e.target.value) || 30 }))}
                />
                <p className="text-xs text-gray-500 mt-1">Sugestão: 20-40 perguntas (mín: 10, máx: 50)</p>
              </div>

              <div>
                <Label htmlFor="ia-tipo">Tipo de Aplicação *</Label>
                <Select 
                  value={iaFormData.tipo} 
                  onValueChange={(value) => setIaFormData(prev => ({ ...prev, tipo: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(tiposAvaliacao).map(([key, tipo]) => {
                      const IconComponent = tipo.icon;
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <IconComponent className="w-4 h-4" />
                            <div>
                              <div>{tipo.nome}</div>
                              <div className="text-xs text-gray-500">{tipo.descricao}</div>
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Info sobre valores da empresa */}
              {valoresIdentidade.length > 0 ? (
                <Alert className="bg-blue-50 border-blue-200">
                  <Target className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-sm text-blue-800">
                    <strong>Valores que serão usados:</strong>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {valoresIdentidade.slice(0, 5).map((valor, idx) => (
                        <Badge key={idx} variant="outline" className="bg-white">
                          {valor.nome}
                        </Badge>
                      ))}
                      {valoresIdentidade.length > 5 && (
                        <Badge variant="outline" className="bg-white">
                          +{valoresIdentidade.length - 5} mais
                        </Badge>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="bg-yellow-50 border-yellow-300">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-sm text-yellow-800">
                    <strong>Atenção:</strong> Nenhum valor cadastrado na Identidade Organizacional. 
                    Configure primeiro os valores da empresa em <strong>Profiler → Identidade Organizacional</strong>.
                  </AlertDescription>
                </Alert>
              )}

              <Alert className="bg-purple-50 border-purple-200">
                <Lightbulb className="h-4 w-4 text-purple-600" />
                <AlertDescription className="text-xs text-purple-800">
                  <strong>Como funciona:</strong> A IA analisará a Identidade Organizacional da sua empresa 
                  (missão, visão, valores e cultura desejada) e gerará perguntas inéditas distribuídas 
                  entre todos os valores. Incluirá automaticamente perguntas invertidas para detectar 
                  inconsistências nas respostas.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsIAModalOpen(false)}
                disabled={isGeneratingIA}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleGerarComIA}
                disabled={isGeneratingIA}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isGeneratingIA ? (
                  <>
                    <Timer className="w-4 h-4 mr-2 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Gerar com IA
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Preview da Avaliação Gerada pela IA */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-600" />
                Preview - Avaliação Gerada pela IA
              </DialogTitle>
              <DialogDescription>
                Revise a avaliação gerada. Você pode salvar diretamente ou editar antes de salvar.
              </DialogDescription>
            </DialogHeader>

            {avaliacaoGeradaIA && (
              <div className="space-y-4">
                {/* Informações Gerais */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{avaliacaoGeradaIA.nome}</CardTitle>
                    <CardDescription>{avaliacaoGeradaIA.descricao}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Total Perguntas</p>
                        <p className="font-semibold">{avaliacaoGeradaIA.total_perguntas}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Perguntas Invertidas</p>
                        <p className="font-semibold text-purple-600">{avaliacaoGeradaIA.perguntas_invertidas}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Tipo</p>
                        <p className="font-semibold capitalize">{avaliacaoGeradaIA.tipo_avaliacao}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Escala</p>
                        <p className="font-semibold">Likert 5 pontos</p>
                      </div>
                    </div>

                    {avaliacaoGeradaIA.resumo_ia && (
                      <Alert className="bg-blue-50 border-blue-200">
                        <Lightbulb className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-sm text-blue-800">
                          <strong>Estratégia da IA:</strong> {avaliacaoGeradaIA.resumo_ia}
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Critérios de Aprovação */}
                    <Alert className="bg-gradient-to-r from-red-50 via-yellow-50 to-green-50 border-gray-300">
                      <Target className="h-4 w-4 text-gray-700" />
                      <AlertDescription className="text-sm">
                        <strong>Critérios de Aprovação Automáticos:</strong>
                        <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                          <div className="bg-red-100 p-2 rounded border border-red-300">
                            <div className="font-semibold text-red-800">Fit Baixo</div>
                            <div className="text-red-600">0% a 30%</div>
                            <div className="text-red-800 font-medium">Reprovado</div>
                          </div>
                          <div className="bg-yellow-100 p-2 rounded border border-yellow-300">
                            <div className="font-semibold text-yellow-800">Fit Médio</div>
                            <div className="text-yellow-600">31% a 60%</div>
                            <div className="text-yellow-800 font-medium">Requer Atenção</div>
                          </div>
                          <div className="bg-green-100 p-2 rounded border border-green-300">
                            <div className="font-semibold text-green-800">Fit Alto</div>
                            <div className="text-green-600">61% a 100%</div>
                            <div className="text-green-800 font-medium">Aprovado</div>
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>

                {/* Lista de Perguntas */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Perguntas Geradas ({avaliacaoGeradaIA.perguntas.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {avaliacaoGeradaIA.perguntas.map((pergunta, idx) => {
                        const valor = valoresIdentidade.find(v => v.id === pergunta.valor_organizacional_id);
                        return (
                          <div 
                            key={idx} 
                            className={`p-3 rounded-lg border ${pergunta.invertida ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-200'}`}
                          >
                            <div className="flex items-start gap-2">
                              <Badge variant="outline" className="mt-0.5">
                                {idx + 1}
                              </Badge>
                              <div className="flex-1">
                                <p className="text-sm">{pergunta.texto}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  {valor && (
                                    <Badge variant="secondary" className="text-xs">
                                      {valor.nome}
                                    </Badge>
                                  )}
                                  {pergunta.invertida && (
                                    <Badge className="text-xs bg-purple-600">
                                      <Shuffle className="w-3 h-3 mr-1" />
                                      Invertida
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsPreviewOpen(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button 
                variant="outline"
                onClick={handleEditarAvaliacaoIA}
                disabled={saving}
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar Perguntas
              </Button>
              <Button 
                onClick={handleSalvarAvaliacaoIA}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving ? (
                  <>
                    <Timer className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Avaliação
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ConfiguracaoAvaliacao;